import React from "react";
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import ExtensionIcon from "@mui/icons-material/Extension";

const STATUS_OPTIONS = [
  { value: "RUNNING",    color: "#1976d2", bg: "#e3f2fd" },
  { value: "PENDING",    color: "#e65100", bg: "#fff3e0" },
  { value: "INITIATED",  color: "#e65100", bg: "#fff3e0" },
  { value: "RETRY",      color: "#6a1b9a", bg: "#f3e5f5" },
  { value: "COMPLETED",  color: "#2e7d32", bg: "#e8f5e9" },
  { value: "FAILED",     color: "#b71c1c", bg: "#ffebee" },
  { value: "INACTIVE",   color: "#424242", bg: "#f5f5f5" },
  { value: "REPLICATED", color: "#01579b", bg: "#e1f5fe" },
];

const SELECT_SX = {
  fontSize: 13,
  height: 38,
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
};

const DATE_SX = {
  width: 152,
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
};

export default function ModuleUsageFilters({ filters, onChange, filterOptions }) {
  const { customers = [], users = [], request_types = [] } = filterOptions || {};

  function set(key, value) {
    onChange({ ...filters, [key]: value, page: 1 });
  }

  function toggleStatus(value) {
    const current = filters.status || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    set("status", next);
  }

  function clearAll() {
    onChange({
      from_date: "", to_date: "",
      customer_id: [], user_id: [], request_type: [], status: [],
      page: 1,
    });
  }

  const activeFilterCount =
    (filters.from_date ? 1 : 0) +
    (filters.to_date ? 1 : 0) +
    (filters.customer_id?.length || 0) +
    (filters.user_id?.length || 0) +
    (filters.request_type?.length || 0) +
    (filters.status?.length || 0);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e8eaf0",
        borderRadius: 2,
        p: "16px 20px",
        mb: 2.5,
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.8}>
        <Stack direction="row" alignItems="center" gap={0.8}>
          <FilterListIcon sx={{ fontSize: 16, color: "#1976d2" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#444", letterSpacing: 0.3 }}>
            FILTERS
          </Typography>
          {activeFilterCount > 0 && (
            <Chip
              label={activeFilterCount}
              size="small"
              sx={{ height: 18, fontSize: 10, bgcolor: "#1976d2", color: "#fff", fontWeight: 700, "& .MuiChip-label": { px: 0.8 } }}
            />
          )}
        </Stack>
        {activeFilterCount > 0 && (
          <Tooltip title="Clear all filters">
            <Chip
              label="Clear all"
              size="small"
              onDelete={clearAll}
              deleteIcon={<CloseIcon sx={{ fontSize: "13px !important" }} />}
              onClick={clearAll}
              sx={{
                height: 22, fontSize: 11, bgcolor: "#fafafa",
                border: "1px solid #e0e0e0", color: "#666", cursor: "pointer",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            />
          </Tooltip>
        )}
      </Stack>

      {/* Row 1 — Date range */}
      <Stack direction="row" gap={2} alignItems="flex-end" mb={1.8}>
        <Box>
          <Typography sx={fieldLabelSx}>
            <CalendarTodayIcon sx={{ fontSize: 10, mr: 0.4 }} /> From
          </Typography>
          <TextField
            type="date"
            size="small"
            value={filters.from_date || ""}
            onChange={(e) => set("from_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { fontSize: 12, height: 20, padding: "8px 10px" } }}
            sx={DATE_SX}
          />
        </Box>
        <Box>
          <Typography sx={fieldLabelSx}>
            <CalendarTodayIcon sx={{ fontSize: 10, mr: 0.4 }} /> To
          </Typography>
          <TextField
            type="date"
            size="small"
            value={filters.to_date || ""}
            onChange={(e) => set("to_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { fontSize: 12, height: 20, padding: "8px 10px" } }}
            sx={DATE_SX}
          />
        </Box>
      </Stack>

      {/* Row 2 — Dropdowns */}
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-end">
        <Box>
          <Typography sx={fieldLabelSx}>
            <BusinessIcon sx={{ fontSize: 10, mr: 0.4 }} /> Customer
          </Typography>
          <FormControl size="small" sx={{ width: 170 }}>
            <Select
              displayEmpty
              multiple
              value={filters.customer_id || []}
              onChange={(e) => set("customer_id", e.target.value)}
              input={<OutlinedInput />}
              renderValue={(selected) =>
                selected.length === 0
                  ? <Typography sx={{ fontSize: 12, color: "#aaa" }}>All customers</Typography>
                  : <Typography sx={{ fontSize: 12, color: "#333" }}>{selected.length} selected</Typography>
              }
              sx={SELECT_SX}
              MenuProps={{ PaperProps: { sx: { width: 170, maxHeight: 240 } } }}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id} sx={{ fontSize: 12, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {(filters.customer_id || []).includes(c.id) ? "✓ " : "   "}{c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>
            <PersonIcon sx={{ fontSize: 10, mr: 0.4 }} /> User
          </Typography>
          <FormControl size="small" sx={{ width: 210 }}>
            <Select
              displayEmpty
              multiple
              value={filters.user_id || []}
              onChange={(e) => set("user_id", e.target.value)}
              input={<OutlinedInput />}
              renderValue={(selected) =>
                selected.length === 0
                  ? <Typography sx={{ fontSize: 12, color: "#aaa" }}>All users</Typography>
                  : <Typography sx={{ fontSize: 12, color: "#333" }}>{selected.length} selected</Typography>
              }
              sx={SELECT_SX}
              MenuProps={{ PaperProps: { sx: { width: 210, maxHeight: 240 } } }}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id} sx={{ fontSize: 12, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {(filters.user_id || []).includes(u.id) ? "✓ " : "   "}{u.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>
            <ExtensionIcon sx={{ fontSize: 10, mr: 0.4 }} /> Module
          </Typography>
          <FormControl size="small" sx={{ width: 170 }}>
            <Select
              displayEmpty
              multiple
              value={filters.request_type || []}
              onChange={(e) => set("request_type", e.target.value)}
              input={<OutlinedInput />}
              renderValue={(selected) =>
                selected.length === 0
                  ? <Typography sx={{ fontSize: 12, color: "#aaa" }}>All modules</Typography>
                  : <Typography sx={{ fontSize: 12, color: "#333" }}>{selected.length} selected</Typography>
              }
              sx={SELECT_SX}
              MenuProps={{ PaperProps: { sx: { width: 170, maxHeight: 240 } } }}
            >
              {request_types.map((rt) => (
                <MenuItem key={rt.value} value={rt.value} sx={{ fontSize: 12 }}>
                  {(filters.request_type || []).includes(rt.value) ? "✓ " : "   "}{rt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>

      {/* Status chips */}
      <Box sx={{ mt: 2, pt: 1.8, borderTop: "1px solid #f0f0f0" }}>
        <Typography sx={{ ...fieldLabelSx, mb: 1 }}>Status</Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {STATUS_OPTIONS.map(({ value, color, bg }) => {
            const active = (filters.status || []).includes(value);
            return (
              <Chip
                key={value}
                label={value}
                size="small"
                onClick={() => toggleStatus(value)}
                sx={{
                  height: 24, fontSize: 11,
                  fontWeight: active ? 700 : 400,
                  bgcolor: active ? color : "#fff",
                  color: active ? "#fff" : color,
                  border: `1px solid ${color}`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: active ? color : bg, opacity: 0.9 },
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* Active selection tags */}
      {((filters.customer_id?.length > 0) || (filters.user_id?.length > 0) || (filters.request_type?.length > 0)) && (
        <Box sx={{ mt: 1.8, pt: 1.5, display: "flex", gap: 0.8, flexWrap: "wrap", borderTop: "1px solid #f0f0f0" }}>
          {(filters.customer_id || []).map((id) => {
            const c = customers.find((x) => x.id === id);
            return c ? (
              <Chip
                key={`c-${id}`}
                label={c.name}
                size="small"
                icon={<BusinessIcon sx={{ fontSize: "11px !important" }} />}
                onDelete={() => set("customer_id", (filters.customer_id || []).filter((x) => x !== id))}
                sx={{ height: 22, fontSize: 11, bgcolor: "#e8f0fe", color: "#1a56db", border: "none" }}
              />
            ) : null;
          })}
          {(filters.user_id || []).map((id) => {
            const u = users.find((x) => x.id === id);
            return u ? (
              <Chip
                key={`u-${id}`}
                label={u.email}
                size="small"
                icon={<PersonIcon sx={{ fontSize: "11px !important" }} />}
                onDelete={() => set("user_id", (filters.user_id || []).filter((x) => x !== id))}
                sx={{ height: 22, fontSize: 11, bgcolor: "#fce8ff", color: "#7e22ce", border: "none" }}
              />
            ) : null;
          })}
          {(filters.request_type || []).map((rt) => {
            const m = request_types.find((x) => x.value === rt);
            return (
              <Chip
                key={`rt-${rt}`}
                label={m?.label || rt}
                size="small"
                icon={<ExtensionIcon sx={{ fontSize: "11px !important" }} />}
                onDelete={() => set("request_type", (filters.request_type || []).filter((x) => x !== rt))}
                sx={{ height: 22, fontSize: 11, bgcolor: "#ecfdf5", color: "#065f46", border: "none" }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

const fieldLabelSx = {
  fontSize: 10,
  fontWeight: 600,
  color: "#888",
  letterSpacing: 0.4,
  textTransform: "uppercase",
  mb: 0.75,
  display: "flex",
  alignItems: "center",
};
